import _regeneratorRuntime from "@babel/runtime/regenerator";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import EthereumTx from 'ethereumjs-tx';
import BigNumber from 'bn.js';
import { warning } from '@colony/purser-core/utils';
import { hexSequenceValidator, addressValidator, safeIntegerValidator } from '@colony/purser-core/validators';
import { addressNormalizer, hexSequenceNormalizer } from '@colony/purser-core/normalizers';
import { transactionObjectValidator, messageVerificationObjectValidator, messageOrDataValidator } from '@colony/purser-core/helpers';
import { HEX_HASH_TYPE } from '@colony/purser-core/defaults';
import { methodCaller } from './helpers';
import { getTransaction as getTransactionMethodLink, signTransaction as signTransactionMethodLink, signMessage as signMessageMethodLink, verifyMessage as verifyMessageMethodLink } from './methodLinks';
import { STD_ERRORS } from './defaults';
import { staticMethods as messages } from './messages';
/**
 * Sign (and send) a transaction object and return the serialized signature (as a hex string)
 *
 * @TODO Refactor to only sign the transaction
 * This is only after Metamask will allow us that functionality (see below)
 *
 * Metamask doesn't currently allow us to sign a transaction without also broadcasting it to
 * the network. See this issue for context:
 * https://github.com/MetaMask/metamask-extension/issues/3475
 *
 * @method signTransaction
 *
 * @param {string} from the sender address (provided by the Wallet instance)
 * @param {bigNumber} gasPrice gas price for the transaction in WEI (as an instance of bigNumber), defaults to 9000000000 (9 GWEI)
 * @param {bigNumber} gasLimit gas limit for the transaction (as an instance of bigNumber), defaults to 21000
 * @param {number} nonce the nonce to use for the transaction (as a number)
 * @param {string} to the address to which to the transaction is sent
 * @param {bigNumber} value the value of the transaction in WEI (as an instance of bigNumber), defaults to 1
 * @param {string} inputData data appended to the transaction (as a `hex` string)
 *
 * All the above params are sent in as props of an object.
 *
 * @return {Promise<string>} the hex signature string
 */

export var signTransaction =
/*#__PURE__*/
function () {
  var _ref = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee2() {
    var _ref2,
        from,
        manualNonce,
        transactionObject,
        _transactionObjectVal,
        gasPrice,
        gasLimit,
        to,
        value,
        inputData,
        _args2 = arguments;

    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _ref2 = _args2.length > 0 && _args2[0] !== undefined ? _args2[0] : {}, from = _ref2.from, manualNonce = _ref2.nonce, transactionObject = _objectWithoutProperties(_ref2, ["from", "nonce"]);
            _transactionObjectVal = transactionObjectValidator(transactionObject), gasPrice = _transactionObjectVal.gasPrice, gasLimit = _transactionObjectVal.gasLimit, to = _transactionObjectVal.to, value = _transactionObjectVal.value, inputData = _transactionObjectVal.inputData;
            addressValidator(from);
            /*
             * Metamask auto-sets the nonce based on the next one available. You can manually
             * override it, but it's best to omit it.
             *
             * So we only validate if there is one, otherwise we just pass undefined
             * to the transaction object.
             *
             * We also notify (in dev mode) the user about not setting the nonce.
             */

            if (manualNonce) {
              safeIntegerValidator(manualNonce);
              warning(messages.dontSetNonce);
            }
            /*
             * We must check for the Metamask injected in-page proxy every time we
             * try to access it. This is because something can change it from the time
             * of last detection until now.
             */


            return _context2.abrupt("return", methodCaller(
            /*
             * @TODO Move into own (non-anonymous) method
             * This way we could better test it
             */
            function () {
              return new Promise(function (resolve) {
                return signTransactionMethodLink(Object.assign({}, {
                  from: addressNormalizer(from),

                  /*
                  * We don't need to normalize these three values since Metamask accepts
                  * number values directly, so we don't need to convert them to hex
                  */
                  value: value.toString(),
                  gas: gasLimit.toString(),
                  gasPrice: gasPrice.toString(),
                  data: hexSequenceNormalizer(inputData),

                  /*
                   * Most likely this value is `undefined`, but that is good (see above)
                   */
                  nonce: manualNonce
                },
                /*
                 * Only send (and normalize) the destingation address if one was
                 * provided in the initial transaction object.
                 */
                typeof to !== 'undefined' ? {
                  to: addressNormalizer(to)
                } : {}),
                /*#__PURE__*/

                /*
                 * @TODO Move into own (non-anonymous) method
                 * This way we could better test it
                 */
                function () {
                  var _ref3 = _asyncToGenerator(
                  /*#__PURE__*/
                  _regeneratorRuntime.mark(function _callee(error, transactionHash) {
                    var normalizedTransactionHash, _ref4, gas, signedGasPrice, signedData, nonce, r, s, signedTo, v, signedValue, signedTransaction, serializedSignedTransaction, normalizedSignedTransaction;

                    return _regeneratorRuntime.wrap(function _callee$(_context) {
                      while (1) {
                        switch (_context.prev = _context.next) {
                          case 0:
                            _context.prev = 0;

                            /*
                             * Validate that the signature hash is in the correct format
                             */
                            hexSequenceValidator(transactionHash);
                            /*
                             * Add the `0x` prefix to the signed transaction hash
                             */

                            normalizedTransactionHash = hexSequenceNormalizer(transactionHash);
                            /*
                             * Get signed transaction object with transaction hash using Web3
                             * Include signature + any values MetaMask may have changed.
                             */

                            _context.next = 5;
                            return getTransactionMethodLink(normalizedTransactionHash);

                          case 5:
                            _ref4 = _context.sent;
                            gas = _ref4.gas;
                            signedGasPrice = _ref4.gasPrice;
                            signedData = _ref4.input;
                            nonce = _ref4.nonce;
                            r = _ref4.r;
                            s = _ref4.s;
                            signedTo = _ref4.to;
                            v = _ref4.v;
                            signedValue = _ref4.value;

                            /*
                             * RLP encode (to hex string) with ethereumjs-tx, prefix with
                             * `0x` and return. Convert to BN all the numbers-as-strings.
                             */
                            signedTransaction = new EthereumTx({
                              data: signedData,
                              gasLimit: new BigNumber(gas),
                              gasPrice: new BigNumber(signedGasPrice),
                              nonce: new BigNumber(nonce),
                              r: r,
                              s: s,
                              to: signedTo,
                              v: v,
                              value: new BigNumber(signedValue)
                            });
                            serializedSignedTransaction = signedTransaction.serialize().toString(HEX_HASH_TYPE);
                            normalizedSignedTransaction = hexSequenceNormalizer(serializedSignedTransaction);
                            return _context.abrupt("return", resolve(normalizedSignedTransaction));

                          case 21:
                            _context.prev = 21;
                            _context.t0 = _context["catch"](0);

                            if (!error.message.includes(STD_ERRORS.CANCEL_TX_SIGN)) {
                              _context.next = 25;
                              break;
                            }

                            return _context.abrupt("return", warning(messages.cancelTransactionSign));

                          case 25:
                            throw new Error(error.message);

                          case 26:
                          case "end":
                            return _context.stop();
                        }
                      }
                    }, _callee, this, [[0, 21]]);
                  }));

                  return function (_x, _x2) {
                    return _ref3.apply(this, arguments);
                  };
                }());
              });
            }, messages.cannotSendTransaction));

          case 5:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, this);
  }));

  return function signTransaction() {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Sign a message and return the signature. Useful for verifying identities.
 *
 * @method signMessage
 *
 * @param {string} currentAddress The current selected address (in the UI)
 * @param {string} message the message you want to sign
 *
 * All the above params are sent in as props of an {object.
 *
 * @return {Promise<string>} The signed message `hex` string (wrapped inside a `Promise`)
 */

export var signMessage =
/*#__PURE__*/
function () {
  var _ref5 = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee3() {
    var _ref6,
        currentAddress,
        message,
        messageData,
        toSign,
        _args3 = arguments;

    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _ref6 = _args3.length > 0 && _args3[0] !== undefined ? _args3[0] : {}, currentAddress = _ref6.currentAddress, message = _ref6.message, messageData = _ref6.messageData;
            addressValidator(currentAddress);
            toSign = messageOrDataValidator({
              message: message,
              messageData: messageData
            });
            /*
             * We must check for the Metamask injected in-page proxy every time we
             * try to access it. This is because something can change it from the time
             * of last detection until now.
             */

            return _context3.abrupt("return", methodCaller(
            /*
             * @TODO Move into own (non-anonymous) method
             * This way we could better test it
             */
            function () {
              return new Promise(function (resolve) {
                /*
                 * Sign the message. This will prompt the user via Metamask's UI
                 */
                signMessageMethodLink(
                /*
                 * Ensure the hex string has the `0x` prefix
                 */
                hexSequenceNormalizer(
                /*
                 * We could really do with default Flow types for Buffer...
                 */

                /* $FlowFixMe */
                Buffer.from(toSign).toString(HEX_HASH_TYPE)), currentAddress,
                /*
                 * @TODO Move into own (non-anonymous) method
                 * This way we could better test it
                 */
                function (error, messageSignature) {
                  try {
                    /*
                     * Validate that the signature is in the correct format
                     */
                    hexSequenceValidator(messageSignature);
                    /*
                     * Add the `0x` prefix to the message's signature
                     */

                    var normalizedSignature = hexSequenceNormalizer(messageSignature);
                    return resolve(normalizedSignature);
                  } catch (caughtError) {
                    /*
                     * Don't throw an Error if the user just cancels signing the message.
                     * This is normal UX, not an exception
                     */
                    if (error.message.includes(STD_ERRORS.CANCEL_MSG_SIGN)) {
                      return warning(messages.cancelMessageSign);
                    }

                    throw new Error(error.message);
                  }
                });
              });
            }, messages.cannotSignMessage));

          case 4:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3, this);
  }));

  return function signMessage() {
    return _ref5.apply(this, arguments);
  };
}();
/**
 * Verify a signed message. Useful for verifying identity. (In conjunction with `signMessage`)
 *
 * @method verifyMessage
 *
 * @param {string} message The message to verify if it was signed correctly
 * @param {string} signature The message signature as a `hex` string (you usually get this via `signMessage`)
 * @param {string} currentAddress The current selected address (in the UI)
 *
 * All the above params are sent in as props of an object.
 *
 * @return {Promise<boolean>} A boolean to indicate if the message/signature pair are valid (wrapped inside a `Promise`)
 */

export var verifyMessage =
/*#__PURE__*/
function () {
  var _ref7 = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee4() {
    var _ref8,
        currentAddress,
        messageVerificationObject,
        _messageVerificationO,
        message,
        signature,
        _args4 = arguments;

    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _ref8 = _args4.length > 0 && _args4[0] !== undefined ? _args4[0] : {}, currentAddress = _ref8.currentAddress, messageVerificationObject = _objectWithoutProperties(_ref8, ["currentAddress"]);

            /*
             * Validate the current address
             */
            addressValidator(currentAddress);
            /*
             * Validate the rest of the pros using the core helper
             */

            _messageVerificationO = messageVerificationObjectValidator(messageVerificationObject), message = _messageVerificationO.message, signature = _messageVerificationO.signature;
            /*
             * We must check for the Metamask injected in-page proxy every time we
             * try to access it. This is because something can change it from the time
             * of last detection until now.
             */

            return _context4.abrupt("return", methodCaller(
            /*
             * @TODO Move into own (non-anonymous) method
             * This way we could better test it
             */
            function () {
              return new Promise(function (resolve) {
                /*
                 * Verify the message
                 */
                verifyMessageMethodLink(message,
                /*
                 * Ensure the signature has the `0x` prefix
                 */
                hexSequenceNormalizer(signature),
                /*
                 * @TODO Move into own (non-anonymous) method
                 * This way we could better test it
                 */
                function (error, recoveredAddress) {
                  try {
                    /*
                     * Validate that the recovered address is correct
                     */
                    addressValidator(recoveredAddress);
                    /*
                     * Add the `0x` prefix to the recovered address
                     */

                    var normalizedRecoveredAddress = addressNormalizer(recoveredAddress);
                    /*
                     * Add the `0x` prefix to the current address
                     */

                    var normalizedCurrentAddress = addressNormalizer(currentAddress);
                    return resolve(normalizedRecoveredAddress === normalizedCurrentAddress);
                  } catch (caughtError) {
                    throw new Error(error.message);
                  }
                });
              });
            }, messages.cannotSignMessage));

          case 4:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4, this);
  }));

  return function verifyMessage() {
    return _ref7.apply(this, arguments);
  };
}();